import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Gift, Ticket, Sparkles, Loader2, CheckCircle2, Clock } from "lucide-react";

export default function LoyaltyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/loyalty/me");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const redeem = async () => {
    setRedeeming(true);
    try {
      const { data: coupon } = await api.post("/loyalty/redeem");
      toast.success(`Cupom ${coupon.code} gerado! Use na sua próxima OS.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Não foi possível resgatar");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return <p className="text-slate-400 text-sm">Carregando...</p>;
  if (!data) return null;

  const pct = Math.min((data.points / data.redeem_cost) * 100, 100);
  const canRedeem = data.points >= data.redeem_cost;
  const activeCoupons = (data.coupons || []).filter((c) => c.status === "ativo");

  return (
    <div className="space-y-5" data-testid="loyalty-tab">
      <Card className="border-none bg-gradient-to-br from-[#0a2a6c] to-[#1e7be0] text-white overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Seus pontos</p>
              <p className="text-4xl font-bold mt-1" data-testid="loyalty-points">{data.points}</p>
              <p className="text-white/70 text-xs mt-1">Ganhe {data.points_per_service} pontos a cada atendimento concluído</p>
            </div>
            <Gift className="h-14 w-14 text-white/30" />
          </div>
          <div className="mt-5">
            <div className="flex justify-between text-xs text-white/80 mb-1">
              <span>Progresso para o próximo cupom</span>
              <span>{data.points} / {data.redeem_cost}</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <Button onClick={redeem} disabled={!canRedeem || redeeming}
            className="mt-4 w-full bg-white text-[#0a2a6c] hover:bg-white/90 gap-1.5 disabled:opacity-60"
            data-testid="redeem-loyalty-button">
            {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />}
            {canRedeem
              ? `Resgatar cupom de R$ ${data.coupon_value.toFixed(2).replace(".", ",")}`
              : `Faltam ${data.redeem_cost - data.points} pontos para um cupom`}
          </Button>
        </CardContent>
      </Card>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Ticket className="h-4 w-4 text-[#1e7be0]" /> Meus cupons</h3>
        {(data.coupons || []).length === 0 ? (
          <p className="text-slate-400 text-sm">Você ainda não resgatou cupons.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2" data-testid="coupons-list">
            {data.coupons.map((c) => (
              <Card key={c.id} className={`border ${c.status === "ativo" ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50"}`} data-testid={`coupon-${c.code}`}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-mono font-bold text-slate-800">{c.code}</p>
                    <p className="text-xs text-slate-500">Desconto de R$ {Number(c.discount).toFixed(2).replace(".", ",")}</p>
                  </div>
                  <Badge className={c.status === "ativo" ? "bg-emerald-100 text-emerald-800 border-emerald-200 gap-1" : "bg-slate-200 text-slate-600 gap-1"}>
                    {c.status === "ativo" ? <><CheckCircle2 className="h-3 w-3" /> Ativo</> : <><Clock className="h-3 w-3" /> Usado</>}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {activeCoupons.length > 0 && (
          <p className="text-xs text-slate-500 mt-2">
            Informe o código do cupom à equipe ao gerar a Ordem de Serviço para aplicar o desconto.
          </p>
        )}
      </div>

      {(data.history || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Histórico de pontos</h3>
          <div className="space-y-1.5" data-testid="loyalty-history">
            {data.history.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-md px-3 py-2">
                <span className="text-slate-600">{h.type === "earn" ? "Atendimento concluído" : "Resgate de cupom"}</span>
                <span className={`font-semibold ${h.points >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {h.points >= 0 ? "+" : ""}{h.points} pts
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
